import {Box, CircularProgress, useTheme} from "@material-ui/core";
import {useCurrentShow, useEffectiveShowLanguages, useOrganiser} from "hooks";
import React, {useEffect, useMemo, useState} from "react";
import {useParams} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {isEqual, pick} from "lodash";
import {RequestKey} from "api/request-key";
import Alert from "view/components/Alert";
import {LocalisationPreviewLanguageBar} from "view/components/Localisation";
import PoweredByTitle from "view/components/PoweredByTitle";
import {
  ENGLISH_LANG_CODE,
  IDefaultEmailTemplate,
  TypeEmailTemplates,
} from "@evennt-tech/api-v2-core";
import useDefaultEmailTemplate from "hooks/emailTemplates/useDefaultEmailTemplate";
import useEmailTemplate from "hooks/emailTemplates/useEmailTemplate";
import useLocalisedEmailTemplate from "hooks/emailTemplates/useLocalisedEmailTemplate";
import {
  PreviewEmailTemplate,
  PreviewEmailTemplateBroadcastChannel,
  PreviewEmailTemplateTranslations,
} from "ts/app/email-template/preview-email-template.constraint";
import {isBlankTranslation} from "tools/translation";
import useBranding from "store/branding/branding.store";
import {useGenerateThemeFromBranding} from "view/apps/Exhibitor/helpers";
import {ThemeProvider} from "@material-ui/styles";
import TaskIsOverdueEmailBody from "./emailBodies/TaskIsOverdue";
import TaskIsCloseDueEmailBody from "./emailBodies/TaskIsCloseDue";
import ExhibitorInviteEmailBody from "./emailBodies/ExhibitorInvite";
import TaskSumbissionEmailBody from "./emailBodies/TaskSubmission";
import {PreviewTranslateProvider} from "./PreviewTranslate";
import useStyles from "./styles";

const defaultEmailTemplateId: TypeEmailTemplates =
  TypeEmailTemplates.EXHIBITOR_INVITE_EMAIL_TEMPLATE;

const EMAIL_TEMPLATE_TEXT_FIELDS: Array<keyof IDefaultEmailTemplate> = [
  "subject",
  "greeting",
  "mainText",
  "secondaryText",
  "emailText",
  "taskText",
  "buttonText",
];

const EmailTemplatePage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [
    emailTemplate,
    setEmailTemplate,
  ] = useState<IDefaultEmailTemplate | null>(null);

  const routerParams = useParams();

  const emailTemplateId: TypeEmailTemplates = window.atob(
    routerParams?.emailTemplateId || defaultEmailTemplateId
  ) as TypeEmailTemplates;

  const params = new URLSearchParams(window.location.search);
  const showId = params.get("showId") || undefined;

  const [previewLanguage, setPreviewLanguage] = useState(
    params.get("languageCode") || ENGLISH_LANG_CODE
  );
  const isEnglishPreview = previewLanguage === ENGLISH_LANG_CODE;
  const {
    effective: previewLanguages,
    defaultLanguage,
  } = useEffectiveShowLanguages(showId);

  const {defaultEmailTemplate} = useDefaultEmailTemplate({
    type: emailTemplateId,
  });

  const [show] = useCurrentShow({showId});

  const {
    emailTemplate: emailTemplateFromShow,
    isLoaded: isShowTemplateLoaded,
  } = useEmailTemplate({
    type: emailTemplateId,
    showId: showId as string,
  });

  const {
    emailTemplate: localisedEmailTemplate,
    isLoading: isLocalisedLoading,
    isMissing: isLocalisedMissing,
  } = useLocalisedEmailTemplate({
    type: emailTemplateId,
    showId,
    isDefault: !emailTemplateFromShow,
    enabled: isShowTemplateLoaded,
    languageCode: previewLanguage,
  });

  const [
    previewEmailTemplate,
    setPreviewEmailTemplate,
  ] = useState<IDefaultEmailTemplate | null>(null);

  const [
    previewTranslations,
    setPreviewTranslations,
  ] = useState<PreviewEmailTemplateTranslations>({});

  /**
   * What the open editor is showing right now, in the selected language:
   * its source text with the staged translations laid over it. Fields left
   * untranslated keep the source text, the way a real send renders them —
   * for a platform default the editor has not changed, that source is the
   * localised default.
   */
  const livePreviewTemplate = useMemo(() => {
    if (!previewEmailTemplate) {
      return null;
    }

    if (isEnglishPreview) {
      return previewEmailTemplate;
    }

    const isUntouchedDefault =
      !emailTemplateFromShow &&
      !!localisedEmailTemplate &&
      isEqual(
        pick(previewEmailTemplate, EMAIL_TEMPLATE_TEXT_FIELDS),
        pick(defaultEmailTemplate, EMAIL_TEMPLATE_TEXT_FIELDS)
      );
    const base = isUntouchedDefault
      ? {...previewEmailTemplate, ...localisedEmailTemplate}
      : previewEmailTemplate;

    const translated = previewTranslations[previewLanguage];

    if (!translated) {
      return base;
    }

    return Object.keys(translated).reduce<IDefaultEmailTemplate>(
      (acc, field) => {
        const text = translated[field as keyof IDefaultEmailTemplate];

        return isBlankTranslation(text) ? acc : {...acc, [field]: text};
      },
      base
    );
  }, [
    previewEmailTemplate,
    previewTranslations,
    previewLanguage,
    isEnglishPreview,
    emailTemplateFromShow,
    localisedEmailTemplate,
    defaultEmailTemplate,
  ]);

  const [organiser] = useOrganiser();

  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = new BroadcastChannel(PreviewEmailTemplate);

    const initMessage: PreviewEmailTemplateBroadcastChannel = {
      type: "reload",
    };

    // Ask any open editing modal to push its current form content for preview.
    channel.postMessage(initMessage);

    channel.onmessage = (
      event: MessageEvent<PreviewEmailTemplateBroadcastChannel>
    ) => {
      const {type, data} = event.data;

      if (type === "update" && data && data.type === emailTemplateId) {
        setPreviewEmailTemplate(data);
        setPreviewTranslations(event.data.translations ?? {});
      }

      if (type === "close-update") {
        setPreviewEmailTemplate(null);
        setPreviewTranslations({});
        // The editor closed — whatever it saved is newer than the copy this
        // tab cached, so drop it rather than fall back to stale text.
        queryClient.invalidateQueries({
          queryKey: [
            RequestKey.GetLocalisedEmailTemplateApiV2,
            showId,
            emailTemplateId,
          ],
        });
      }
    };

    return () => {
      channel.close();
    };
  }, [emailTemplateId, showId, queryClient]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (!isEnglishPreview) {
      if (livePreviewTemplate) {
        setEmailTemplate(livePreviewTemplate);

        return;
      }

      if (isLocalisedLoading || !isShowTemplateLoaded) {
        return;
      }

      setEmailTemplate(
        localisedEmailTemplate ?? emailTemplateFromShow ?? defaultEmailTemplate
      );

      return;
    }

    if (livePreviewTemplate) {
      setEmailTemplate(livePreviewTemplate);

      return;
    }

    if (emailTemplateFromShow) {
      setEmailTemplate(emailTemplateFromShow);

      return;
    }

    if (defaultEmailTemplate) {
      setEmailTemplate(defaultEmailTemplate);
    }
  }, [
    show,
    emailTemplate,
    emailTemplateFromShow,
    setEmailTemplate,
    defaultEmailTemplate,
    previewEmailTemplate,
    emailTemplateId,
    isEnglishPreview,
    isLocalisedLoading,
    isShowTemplateLoaded,
    localisedEmailTemplate,
    livePreviewTemplate,
  ]);

  if (!emailTemplate || !show || !organiser) {
    return (
      <Box className={classes.loader}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  const renderEmailTemplateBody = () => {
    switch (emailTemplateId) {
      case TypeEmailTemplates.EXHIBITOR_INVITE_EMAIL_TEMPLATE:
        return (
          <ExhibitorInviteEmailBody
            emailTemplate={emailTemplate}
            show={show}
            organiser={organiser}
          />
        );
      case TypeEmailTemplates.TASK_OVERDUE_EMAIL_TEMPLATE:
        return (
          <TaskIsOverdueEmailBody emailTemplate={emailTemplate} show={show} />
        );
      case TypeEmailTemplates.TASK_DUE_EMAIL_TEMPLATE:
        return (
          <TaskIsCloseDueEmailBody emailTemplate={emailTemplate} show={show} />
        );
      case TypeEmailTemplates.TASK_SUBMISSION_EMAIL_TEMPLATE:
        return (
          <TaskSumbissionEmailBody emailTemplate={emailTemplate} show={show} />
        );
      default:
        return <div />;
    }
  };

  return (
    <Box className={classes.page}>
      {previewLanguages.length > 1 && (
        <Box className={classes.languageBar}>
          <LocalisationPreviewLanguageBar
            id="email-preview-language-picker"
            languages={previewLanguages}
            currentCode={previewLanguage}
            defaultCode={defaultLanguage}
            onSelect={setPreviewLanguage}
          />
          {isLocalisedMissing && !livePreviewTemplate && (
            <Alert severity="info">
              The translated version could not be loaded — showing the English
              (UK) text.
            </Alert>
          )}
        </Box>
      )}
      <Box className={classes.emailContainer}>
        <Box mt={22}>
          <PreviewTranslateProvider languageCode={previewLanguage}>
            {renderEmailTemplateBody()}
          </PreviewTranslateProvider>
        </Box>
        <Box className={classes.poweredByContainer}>
          <PoweredByTitle
            color="blue"
            ffairNameColor={theme.palette.contrastText.main}
          />
        </Box>
      </Box>
    </Box>
  );
};

const EmailTemplatePageWrapper = () => {
  const params = new URLSearchParams(window.location.search);
  const showId = params.get("showId") || "";

  const {getBranding, fetch, autoCatch} = useBranding();
  const branding = getBranding(showId);

  useEffect(() => {
    fetch(showId);
    autoCatch(showId);
  }, [showId]);

  const exhibitorShowTheme = useGenerateThemeFromBranding(branding);

  return (
    <ThemeProvider theme={exhibitorShowTheme}>
      <EmailTemplatePage />
    </ThemeProvider>
  );
};

export default EmailTemplatePageWrapper;
